import { useEffect } from "react";
import { Link } from "react-router";
import { motion, AnimatePresence, type Variants } from "framer-motion";
import { useIsMenuOpen, useSetMenuOpen } from "@/stores/uiStore";
import { usePublishedSocialLinks } from "@/stores/socialLinksStore";

const menuItems = [
  { label: "Home", path: "/" },
  { label: "About", path: "/#about-me" },
  { label: "Projects", path: "/project" },
  { label: "Blog", path: "/blog" },
];

const menuEasing = [0.3, 0, 0.3, 1] as const;

// Background overlay: skew slide-in/out (matches original CSS keyframes)
const overlayBgVariants: Variants = {
  hidden: {
    y: "-100%",
    skewX: -10,
    skewY: -10,
  },
  visible: {
    y: 0,
    skewX: 0,
    skewY: 0,
    transition: { duration: 1, ease: menuEasing },
  },
  exit: {
    y: "-100%",
    skewX: -10,
    skewY: -10,
    transition: { duration: 1, ease: menuEasing },
  },
};

// Menu items container with stagger
const menuListVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.6,
    },
  },
  exit: {
    transition: {
      staggerChildren: 0.05,
      staggerDirection: -1,
    },
  },
};

// Individual menu item (matches original GSAP: opacity 0→1, y 20→0, 0.6s, power3.out)
const menuItemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.33, 1, 0.68, 1] }, // power3.out equivalent
  },
  exit: {
    opacity: 0,
    y: 20,
    transition: { duration: 0.3 }, // 2x faster on close (original timeScale(2))
  },
};

// Social links container with stagger
const socialContainerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.05,
      delayChildren: 1.2,
    },
  },
  exit: {
    transition: {
      staggerChildren: 0.025,
      staggerDirection: -1,
    },
  },
};

// Individual social link (matches original GSAP: opacity 0→1, y 20→0, 0.4s)
const socialItemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 },
  },
  exit: {
    opacity: 0,
    y: 20,
    transition: { duration: 0.2 }, // 2x faster on close
  },
};

export default function NavigationOverlay() {
  const isOpen = useIsMenuOpen();
  const setMenuOpen = useSetMenuOpen();
  const socialLinks = usePublishedSocialLinks();

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [isOpen]);

  const handleAnchorClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    const targetId = path.slice(2);
    setMenuOpen(false);

    setTimeout(() => {
      if (window.location.pathname === "/") {
        document
          .getElementById(targetId)
          ?.scrollIntoView({ behavior: "smooth" });
      } else {
        window.location.href = path;
      }
    }, 300);
  };

  const handleLinkClick = () => setMenuOpen(false);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-40"
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Background overlay with skew animation */}
          <motion.div
            className="absolute left-[-10vw] top-[-20vh] w-[120vw] h-[120vh] bg-[#01010b]"
            variants={overlayBgVariants}
          />

          {/* Menu content */}
          <nav className="relative z-10 flex flex-col justify-center items-center min-h-screen py-20 px-5">
            <motion.ul
              className="list-none p-0 m-0 text-center"
              variants={menuListVariants}
            >
              {menuItems.map((item) => (
                <motion.li key={item.path} variants={menuItemVariants}>
                  {item.path.startsWith("/#") ? (
                    <a
                      href={item.path}
                      onClick={(e) => handleAnchorClick(e, item.path)}
                      className="inline-block font-display text-[clamp(2rem,5vw,4rem)] font-bold text-white no-underline py-2 transition-colors duration-300 hover:text-accent"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      viewTransition
                      onClick={handleLinkClick}
                      className="inline-block font-display text-[clamp(2rem,5vw,4rem)] font-bold text-white no-underline py-2 transition-colors duration-300 hover:text-accent"
                    >
                      {item.label}
                    </Link>
                  )}
                </motion.li>
              ))}
            </motion.ul>

            <motion.div
              className="flex gap-6 mt-12"
              variants={socialContainerVariants}
            >
              {socialLinks.map((link) => (
                <motion.a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm uppercase tracking-[0.1em] text-[#bab3e2] no-underline transition-colors duration-300 hover:text-white"
                  variants={socialItemVariants}
                >
                  {link.platform.charAt(0).toUpperCase() +
                    link.platform.slice(1)}
                </motion.a>
              ))}
            </motion.div>
          </nav>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
